import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Mail, Plus, ShieldCheck, Store, UserX, Users as UsersIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import {
    AdminPage,
    ConfirmActionDialog,
    EmptyState,
    KpiCard,
    PillTabs,
    SearchField,
    SectionCard,
    TablePagination,
    formatNumber,
} from '../../../components/design-system/admin';
import { User } from '../../../common/models/user.model';
import { UsersAsList } from '../../../modules/superadmin/users/components/users-as-list.component';
import { useInvitationsApi } from '../../../common/hooks/use-invitations-api.hook';
import {
    AdminUser,
    RoleFilter,
    fullName,
    isStaff,
    isUserEnabled,
    isVendorUser,
    matchesRole,
    matchesSearch,
} from '../../../modules/superadmin/users/users.utils';
import { useUsersApi } from '../../../common/hooks/use-users-api.hook';

const PAGE_SIZES = [10, 25, 50];

/**
 * Everyone who can sign in to the backoffice. The whole list is loaded once
 * (GET /users has no paging); counts, role filter, search and paging are
 * worked out here.
 */
export const Users = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const usersAPI = useUsersApi();
    const invitationsAPI = useInvitationsApi();
    // How many invitations are waiting, for the badge on the Invitations button.
    const [pendingInvitations, setPendingInvitations] = useState(0);

    const [displayedUsers, setDisplayedUsers] = useState<AdminUser[]>([]);
    const [filter, setFilter] = useState('');
    const [role, setRole] = useState<RoleFilter>('all');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    // Each dialog keeps its user after closing so the text doesn't blank out mid-animation.
    const [userToDelete, setUserToDelete] = useState<AdminUser | null>(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [userToToggle, setUserToToggle] = useState<User | null>(null);
    const [disableOpen, setDisableOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const users = await usersAPI.findAllUsers();
            setDisplayedUsers(Array.isArray(users) ? users : []);
            setLoadFailed(false);
        } catch (error) {
            console.error('Error fetching users:', error);
            setLoadFailed(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
        invitationsAPI
            .listInvitations('pending')
            .then((list) => setPendingInvitations(Array.isArray(list) ? list.length : 0))
            .catch(() => setPendingInvitations(0));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const roleLabel = useCallback(
        (r: string) =>
            r === 'SUPER_ADMIN' || r === 'VENDOR_ADMIN'
                ? t(`ADMIN_USERS.ROLES.${r}`)
                : r.replace(/_/g, ' ').toLowerCase(),
        [t],
    );

    const nameOf = (user: User) => fullName(user) || user.email || t('ADMIN_USERS.UNKNOWN');

    // Delete: always asks first.
    const onDelete = (user: AdminUser) => {
        setUserToDelete(user);
        setDeleteOpen(true);
    };

    const handleRemoveUser = async (user: AdminUser) => {
        setBusy(true);
        try {
            await usersAPI.findOneUserAndDelete(user._id);
            toast.success(t('ADMIN_USERS.DELETE_DIALOG.DONE'));
            await fetchUsers();
        } catch (error) {
            console.error('Error deleting user:', error);
            toast.error(t('ADMIN_USERS.DELETE_DIALOG.FAILED'));
        } finally {
            setBusy(false);
            setDeleteOpen(false);
        }
    };

    const handleFilterChange = (value: string) => {
        setFilter(value);
        setPage(0);
    };

    const handleRoleChange = (value: RoleFilter) => {
        setRole(value);
        setPage(0);
    };

    const handleEditUser = (user: User) => {
        navigate(`/users/${user._id}/edit`);
    };

    // New users are invited: they choose their own password.
    const handleAddNewUser = () => {
        navigate('/users/new');
    };

    const handleToggleUserStatus = (user: User) => {
        const willBeDisabled = user.isEnabled !== false;

        // Only ask when disabling a user; enabling happens straight away.
        if (willBeDisabled) {
            setUserToToggle(user);
            setDisableOpen(true);
        } else {
            performToggleUserStatus(user);
        }
    };

    const handleConfirmDisableUser = async () => {
        const user = userToToggle;
        if (user) {
            setBusy(true);
            await performToggleUserStatus(user);
            setBusy(false);
        }
        setDisableOpen(false);
    };

    const handleCancelDisableUser = () => {
        setDisableOpen(false);
    };

    const performToggleUserStatus = async (user: User) => {
        try {
            const newStatus = user.isEnabled !== false ? false : true;
            // PATCH /users with the whole user and the new isEnabled, as always.
            await usersAPI.updateUser({ ...user, isEnabled: newStatus });
            toast.success(t(newStatus ? 'ADMIN_USERS.TOGGLE.ENABLED' : 'ADMIN_USERS.TOGGLE.DISABLED', { name: nameOf(user) }));
            await fetchUsers();
        } catch (error) {
            console.error('Error toggling user status:', error);
            toast.error(t('ADMIN_USERS.TOGGLE.FAILED'));
        }
    };

    // Counts, from the loaded list.
    const staffUsers = displayedUsers.filter(isStaff).length;
    const vendorUsers = displayedUsers.filter(isVendorUser);
    const vendorsWithUsers = new Set(vendorUsers.map((u) => u.vendor?._id).filter(Boolean)).size;
    const disabledUsers = displayedUsers.filter((u) => !isUserEnabled(u)).length;
    const enabledUsers = displayedUsers.length - disabledUsers;

    const filteredUsers = useMemo(
        () => displayedUsers.filter((u) => matchesRole(u, role) && matchesSearch(u, filter, roleLabel)),
        [displayedUsers, role, filter, roleLabel],
    );

    // A refetch can shorten the list under the current page: stay on the last one.
    const lastPage = Math.max(0, Math.ceil(filteredUsers.length / pageSize) - 1);
    const currentPage = Math.min(page, lastPage);
    const pageRows = filteredUsers.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

    const firstLoad = loading && displayedUsers.length === 0;

    return (
        <AdminPage
            title={t('ADMIN_SHELL.NAV.USERS')}
            description={t('ADMIN_USERS.DESCRIPTION')}
            actions={
                <>
                    <Button
                        variant="outline"
                        onClick={() => navigate('/users/invitations')}
                        aria-label={pendingInvitations
                            ? t('ADMIN_USERS.INVITATIONS_BUTTON_PENDING', { count: pendingInvitations })
                            : t('ADMIN_USERS.INVITATIONS_BUTTON')}
                    >
                        <Mail />
                        {t('ADMIN_USERS.INVITATIONS_BUTTON')}
                        {pendingInvitations > 0 && (
                            <Badge variant="secondary" data-testid="pending-invitations-count">{pendingInvitations}</Badge>
                        )}
                    </Button>
                    <Button onClick={handleAddNewUser}>
                        <Plus />
                        {t('ADMIN_USERS.ADD')}
                    </Button>
                </>
            }
        >
            {loadFailed && displayedUsers.length === 0 ? (
                <EmptyState
                    title={t('ADMIN_SHELL.COMMON.LOAD_FAILED')}
                    action={
                        <Button variant="outline" onClick={fetchUsers}>
                            {t('ADMIN_SHELL.COMMON.RETRY')}
                        </Button>
                    }
                    className="py-24"
                />
            ) : (
                <>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="users-kpis">
                        <KpiCard
                            label={t('ADMIN_USERS.KPI.TOTAL')}
                            value={formatNumber(displayedUsers.length)}
                            hint={t('ADMIN_USERS.KPI.TOTAL_HINT', { enabled: formatNumber(enabledUsers) })}
                            icon={<UsersIcon />}
                            loading={firstLoad}
                            testId="kpi-users-total"
                        />
                        <KpiCard
                            label={t('ADMIN_USERS.KPI.STAFF')}
                            value={formatNumber(staffUsers)}
                            hint={t('ADMIN_USERS.KPI.STAFF_HINT')}
                            icon={<ShieldCheck />}
                            loading={firstLoad}
                            testId="kpi-users-staff"
                        />
                        <KpiCard
                            label={t('ADMIN_USERS.KPI.VENDOR')}
                            value={formatNumber(vendorUsers.length)}
                            hint={t('ADMIN_USERS.KPI.VENDOR_HINT', { count: vendorsWithUsers })}
                            icon={<Store />}
                            loading={firstLoad}
                            testId="kpi-users-vendor"
                        />
                        <KpiCard
                            label={t('ADMIN_USERS.KPI.DISABLED')}
                            value={formatNumber(disabledUsers)}
                            hint={t('ADMIN_USERS.KPI.DISABLED_HINT')}
                            icon={<UserX />}
                            loading={firstLoad}
                            testId="kpi-users-disabled"
                        />
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <PillTabs<RoleFilter>
                            value={role}
                            onChange={handleRoleChange}
                            ariaLabel={t('ADMIN_USERS.FILTER.LABEL')}
                            options={[
                                { value: 'all', label: t('ADMIN_USERS.FILTER.ALL'), count: displayedUsers.length },
                                { value: 'staff', label: t('ADMIN_USERS.FILTER.STAFF'), count: staffUsers },
                                { value: 'vendor', label: t('ADMIN_USERS.FILTER.VENDOR'), count: vendorUsers.length },
                            ]}
                        />
                        <SearchField
                            value={filter}
                            onChange={handleFilterChange}
                            placeholder={t('ADMIN_USERS.SEARCH_PLACEHOLDER')}
                        />
                    </div>

                    {firstLoad ? (
                        <Skeleton className="h-96 rounded-xl" />
                    ) : filteredUsers.length === 0 ? (
                        <SectionCard>
                            {displayedUsers.length === 0 ? (
                                <EmptyState
                                    icon={<UsersIcon />}
                                    title={t('ADMIN_USERS.EMPTY.TITLE')}
                                    description={t('ADMIN_USERS.EMPTY.DESCRIPTION')}
                                    action={
                                        <Button onClick={handleAddNewUser}>
                                            <Plus />
                                            {t('ADMIN_USERS.ADD')}
                                        </Button>
                                    }
                                />
                            ) : (
                                <EmptyState
                                    icon={<UsersIcon />}
                                    title={t('ADMIN_USERS.NO_MATCH.TITLE')}
                                    description={t('ADMIN_USERS.NO_MATCH.DESCRIPTION')}
                                    action={
                                        (filter || role !== 'all') && (
                                            <Button
                                                variant="outline"
                                                onClick={() => {
                                                    handleFilterChange('');
                                                    handleRoleChange('all');
                                                }}
                                            >
                                                {t('ADMIN_SHELL.COMMON.CLEAR')}
                                            </Button>
                                        )
                                    }
                                />
                            )}
                        </SectionCard>
                    ) : (
                        <SectionCard flush className="pt-0" testId="users-table">
                            <UsersAsList
                                users={pageRows}
                                handleEditUser={handleEditUser}
                                onDelete={onDelete}
                                onToggleStatus={handleToggleUserStatus}
                                roleLabel={roleLabel}
                            />
                            <TablePagination
                                className="border-t"
                                page={currentPage}
                                pageSize={pageSize}
                                total={filteredUsers.length}
                                onPageChange={setPage}
                                onPageSizeChange={(size) => {
                                    setPageSize(size);
                                    setPage(0);
                                }}
                                pageSizes={PAGE_SIZES}
                            />
                        </SectionCard>
                    )}
                </>
            )}

            <ConfirmActionDialog
                open={deleteOpen}
                onOpenChange={setDeleteOpen}
                title={t('ADMIN_USERS.DELETE_DIALOG.TITLE')}
                description={userToDelete ? t('ADMIN_USERS.DELETE_DIALOG.DESCRIPTION', { name: nameOf(userToDelete) }) : undefined}
                confirmLabel={t('ADMIN_SHELL.COMMON.DELETE')}
                destructive
                busy={busy}
                onConfirm={() => {
                    if (userToDelete) return handleRemoveUser(userToDelete);
                }}
            />

            <ConfirmActionDialog
                open={disableOpen}
                onOpenChange={(open) => !open && handleCancelDisableUser()}
                title={t('ADMIN_USERS.DISABLE_DIALOG.TITLE')}
                description={userToToggle ? t('ADMIN_USERS.DISABLE_DIALOG.DESCRIPTION', { name: nameOf(userToToggle) }) : undefined}
                confirmLabel={t('ADMIN_USERS.DISABLE_DIALOG.CONFIRM')}
                destructive
                busy={busy}
                onConfirm={handleConfirmDisableUser}
            />
        </AdminPage>
    );
};
